import {
  ADD_PROMPTS,
  REMOVE_PROMPT,
  FETCHING_PROMPTS,
  FETCHED_PROMPTS,
  CLEAR_ALL,
  ADD_PROJECTS,
  ADD_QUERIES,
  APPEND_QUERY,
  FETCHING_QUERIES,
  FETCHED_QUERIES,
  UPDATE_QUERY,
  SET_ACTIVE_CONVERSATION,
  SET_ACTIVE_CONVERSATION_FOCUS,
  LOCATION_CHANGE
} from "../actions/constants";
import defaults from "./defaults";

const promptsReducer = (state = defaults.prompts, { type, payload }) => {
  switch (type) {
    case ADD_PROMPTS:
      return { ...state, list: { ...state.list, [payload.project_id]: payload.list }, prompt_reminders: payload.prompt_reminders };
    case REMOVE_PROMPT:
      const current = state.list[payload.project_id];
      const remaining = current.filter((d) => d.schedule_id !== payload.schedule_id);
      return { ...state, list: { ...state.list, [payload.project_id]: remaining } };
    case UPDATE_QUERY: {
      const updating_target_list = state.queries.list[payload.project_id];
      const updating_target = updating_target_list.find((d) => d.id === payload.id);
      const updating_filtered = updating_target_list.filter((d) => d.id !== payload.id);
      const updating_with_updates = { ...updating_target, ...payload };
      
      // Re-sort after update to maintain chronological order (same logic as APPEND_QUERY)
      const updated = [...updating_filtered, updating_with_updates].sort((a,b) => {
        // Parse timestamps robustly
        const at = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
        
        // Check for invalid dates
        const atValid = !isNaN(at) && at > 0;
        const btValid = !isNaN(bt) && bt > 0;
        
        // If both have valid timestamps and they differ, sort by timestamp
        if (atValid && btValid && at !== bt) return at - bt;
        
        // If only one has valid timestamp, valid one comes first
        if (atValid && !btValid) return -1;
        if (!atValid && btValid) return 1;
        
        // Fallback to ID comparison for timestamp ties or both invalid
        const aIsNum = typeof a.id === 'number';
        const bIsNum = typeof b.id === 'number';
        
        if (aIsNum && bIsNum) return a.id - b.id;
        if (aIsNum && !bIsNum) return -1;
        if (!aIsNum && bIsNum) return 1;
        
        // Both are strings - lexicographic compare
        return String(a.id).localeCompare(String(b.id));
      });
      
      return { ...state, queries: { ...state.queries, list: { ...state.queries.list, [payload.project_id]: updated } } };
    }
    case FETCHING_PROMPTS:
      return { ...state, fetching: payload };
    case FETCHED_PROMPTS:
      return { ...state, fetched: { ...state.fetched, [payload.project_id]: new Date() } };
    case ADD_QUERIES:
      return { 
        ...state, 
        queries: { 
          ...state.queries, 
          list: { ...state.queries?.list, [payload.project_id]: payload.list }
        } 
      };
    case APPEND_QUERY: {
      const existingList = state.queries.list[payload.project_id] || [];
      const incoming = { ...payload.query };
      
      if (!incoming.created_at) incoming.created_at = new Date().toISOString();
      
      const existingIndex = existingList.findIndex((q) => q.id === incoming.id);
      
      let updated;
      if (existingIndex !== -1) {
        // UPDATE: merge metadata and replace
        const existing = existingList[existingIndex];
        
        let existingMeta = {};
        let incomingMeta = {};
        try { existingMeta = typeof existing.metadata === 'string' ? JSON.parse(existing.metadata) : (existing.metadata || {}); } catch (e) {}
        try { incomingMeta = typeof incoming.metadata === 'string' ? JSON.parse(incoming.metadata) : (incoming.metadata || {}); } catch (e) {}
        
        const merged = {
          ...existing,
          ...incoming,
          created_at: existing.created_at || incoming.created_at,
          metadata: { ...existingMeta, ...incomingMeta }
        };
        
        updated = [
          ...existingList.slice(0, existingIndex),
          merged,
          ...existingList.slice(existingIndex + 1)
        ];
      } else {
        // INSERT: add new query
        updated = [...existingList, incoming];
      }
      
      // Sort by created_at, then by id
      updated.sort((a, b) => {
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        if (timeA !== timeB) return timeA - timeB;
        return (a.id || 0) - (b.id || 0);
      });
      
      return {
        ...state,
        queries: {
          ...state.queries,
          list: {
            ...state.queries.list,
            [payload.project_id]: updated
          }
        }
      };
    }
    case FETCHING_QUERIES:
      return { ...state, queries: {...state.queries, fetching: payload } };
    case FETCHED_QUERIES:
      return { ...state, queries: {...state.queries, fetched: { ...state.queries?.fetched, [payload.project_id]: new Date() } } };
    case SET_ACTIVE_CONVERSATION:
      return { ...state, activeConversationId: payload, activeConversationFocusId: null };
    case SET_ACTIVE_CONVERSATION_FOCUS:
      return { ...state, activeConversationFocusId: payload };
    case ADD_PROJECTS:
      return { ...state, fetched: {}, fetching: false, list: {}, queries: { list: {}, fetching: false, fetched: {} } };
    case LOCATION_CHANGE:
      // Only clear conversation when navigating AWAY from KB page (not on refreshes/same-page changes)
      const leavingKBPage = payload?.prevPath?.includes('/kb') && !payload?.path?.includes('/kb');
      return leavingKBPage 
        ? { ...state, activeConversationId: null, activeConversationFocusId: null }
        : state;
    case CLEAR_ALL:
      return defaults.prompts;
    default:
      return state;
  }
};

export default promptsReducer;